import { Box, CircularProgress, Dialog } from "@mui/material";
import React, { type ReactNode } from "react";

interface ImageZoomDialogProps {
	open: boolean;
	onClose: () => void;
	/** Object URL of the picture; empty while its bytes are still loading. */
	src: string;
	alt: string;
	/**
	 * What was true when the picture was taken, shown under it. Outside the
	 * click-to-close area, so it can be read and selected — a support ticket's
	 * browser context is something an operator copies out.
	 */
	details?: ReactNode;
}

/**
 * A picture, as large as the window allows, and what it came with.
 *
 * The dialog sizes itself to the image (no `fullWidth`, no fixed height), so
 * there is no frame around it and no empty space under it; a click on the
 * picture closes it. Release-note images and ticket screenshots open through
 * this — the ticket ones carrying the page, browser and console errors
 * recorded at the moment of the capture.
 */
const ImageZoomDialog: React.FC<ImageZoomDialogProps> = ({
	open,
	onClose,
	src,
	alt,
	details,
}) => (
	<Dialog
		data-component="image-zoom-dialog"
		open={open}
		onClose={onClose}
		maxWidth={false}
	>
		<Box
			data-component="image-zoom-content"
			onClick={onClose}
			sx={{ overflow: "auto", cursor: "zoom-out", lineHeight: 0 }}
		>
			{src ? (
				<Box
					component="img"
					src={src}
					alt={alt}
					sx={{ display: "block", maxWidth: "100%" }}
				/>
			) : (
				<Box sx={{ p: 4 }}>
					<CircularProgress />
				</Box>
			)}
		</Box>
		{details}
	</Dialog>
);

export default ImageZoomDialog;
